import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

/**
 * RedirectHandler - Client-side redirect handler
 * 
 * Works alongside server-side redirects to ensure redirects work
 * in SPA environments where the frontend proxy handles routing.
 * 
 * Features:
 * - Checks database for active redirects on route change
 * - Performs client-side navigation to destination
 * - Does NOT affect meta tags, GA, or GSC
 * - Changes apply immediately (patch-based)
 */
const RedirectHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    const checkRedirect = async () => {
      // Skip admin routes, API routes, and SEO files
      const path = location.pathname;
      if (
        path.startsWith('/admin') ||
        path.startsWith('/api') ||
        path === '/sitemap.xml' ||
        path === '/robots.txt' ||
        path === '/llms.txt'
      ) {
        return;
      }

      setChecking(true);
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        // Check if there's a redirect for this path
        const response = await axios.get(`${BACKEND_URL}/api/redirects/check?path=${encodeURIComponent(path)}`);
        
        if (response.data.redirect) {
          const { destination_url, redirect_type } = response.data;
          
          // For 301/308 (permanent), use replace to not add to history
          // For 302/307 (temporary), use push
          if (redirect_type === '301' || redirect_type === '308') {
            navigate(destination_url, { replace: true });
          } else {
            navigate(destination_url);
          }
        }
      } catch (error) {
        // Silently fail - redirects are enhancement, not critical
        console.debug('Redirect check failed:', error.message);
      } finally {
        setChecking(false);
      }
    };

    checkRedirect();
  }, [location.pathname, navigate]);

  // This component doesn't render anything visible
  return null;
};

export default RedirectHandler;
